import { useState } from "react"
import { Camera, X } from "lucide-react"
import { FrameStatusTile } from "@/components/media/frame-status-tile"
import { SketchDialog } from "@/components/sketch/sketch-dialog"
import { confirm } from "@/lib/confirm"
import { formatMs } from "@/lib/format-ms"
import type { ShotFrameCaptureRow } from "@/lib/shot-frame-captures-api"
import { cn } from "@/lib/utils"
import { useTranslation } from "@/hooks/use-translation"

type Props = {
  projectId: number
  captures: ShotFrameCaptureRow[]
  /** Аспект плитки под формат кадра проекта (frameFormatClasses().aspect). */
  aspectClass: string
  /** false — ✕ удаления скрыт (нет can_edit_content). */
  canEdit: boolean
  onDelete: (capture: ShotFrameCaptureRow) => void
  className?: string
}

/**
 * Полоса «Скриншоты» под панелью ассетов шота: плитки кадров с таймкодом,
 * спиннеры для pending/processing, плитки ошибок, просмотр в Sketch view.
 * Видна, пока есть хоть один скриншот — даже если видео у шота уже нет.
 */
export function ShotFrameCapturesStrip({ projectId, captures, aspectClass, canEdit, onDelete, className }: Props) {
  const { t } = useTranslation(["shot"])
  const [zoomed, setZoomed] = useState<ShotFrameCaptureRow | null>(null)

  if (captures.length === 0) return null

  const handleDelete = async (capture: ShotFrameCaptureRow) => {
    if (!(await confirm({ message: t("frameCaptures.deleteConfirm"), tone: "danger" }))) return
    if (zoomed?.id === capture.id) setZoomed(null)
    onDelete(capture)
  }

  return (
    <div className={cn("min-w-0", className)}>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">
        <Camera className="h-3 w-3" /> {t("frameCaptures.title")}
      </div>
      <div className="flex flex-wrap gap-2">
        {captures.map((capture) => (
          <div key={capture.id} className="relative flex flex-col items-center gap-1">
            {capture.status === "ready" && capture.image_url ? (
              <button
                type="button"
                className="cursor-zoom-in transition-opacity hover:opacity-80"
                onClick={() => setZoomed(capture)}
              >
                <FrameStatusTile
                  status={capture.status}
                  url={capture.image_thumb_url ?? capture.image_url}
                  alt={formatMs(capture.timecode_ms)}
                  className={cn("h-16", aspectClass)}
                />
              </button>
            ) : (
              <FrameStatusTile
                status={capture.status}
                url={null}
                fallbackLabel={t("frameCaptures.failed")}
                errorTitle={capture.error_message}
                className={cn("h-16", aspectClass)}
              />
            )}
            <span className="text-[9px] text-ui-text-muted tabular-nums">{formatMs(capture.timecode_ms)}</span>
            {canEdit ? (
              <button
                type="button"
                onClick={() => void handleDelete(capture)}
                className="absolute top-0.5 right-0.5 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-[var(--ui-chip-bg)] text-ui-text-secondary transition-colors hover:bg-red-600/80 hover:text-ui-text-primary"
                aria-label={t("frameCaptures.delete")}
              >
                <X className="h-3 w-3" />
              </button>
            ) : null}
          </div>
        ))}
      </div>
      <SketchDialog
        open={zoomed != null}
        onOpenChange={(open) => {
          if (!open) setZoomed(null)
        }}
        projectId={projectId}
        sourceUrl={zoomed?.image_download_url ?? zoomed?.image_url ?? null}
        presentation="view"
        allowEdit={false}
        persist="none"
        nested
        headerTitle={zoomed ? formatMs(zoomed.timecode_ms) : null}
      />
    </div>
  )
}
